import React from 'react';
import { Card, Row, Col, Progress, Space, Typography } from 'antd';
import { RocketOutlined, TrophyOutlined } from '@ant-design/icons';
import AnalyticsCard from '../../components/shared/AnalyticsCard';
import ChartContainer from '../../components/shared/ChartContainer';
import { mockClientAnalyticsData, mockEngagementMetrics, mockClientLifecycle } from '../data/mockData';

const { Text } = Typography;

const EngagementMetrics: React.FC = () => {
  const { engagement } = mockClientAnalyticsData;

  // Engagement metrics chart
  const engagementChartData = {
    labels: mockEngagementMetrics.map(item => item.metric),
    datasets: [
      {
        label: 'Current Value (%)',
        data: mockEngagementMetrics.map(item => item.value),
        backgroundColor: 'rgba(24, 144, 255, 0.6)',
        borderColor: '#1890ff',
        borderWidth: 1,
      },
      {
        label: 'Target (%)',
        data: mockEngagementMetrics.map(item => item.target),
        backgroundColor: 'rgba(82, 196, 26, 0.6)',
        borderColor: '#52c41a',
        borderWidth: 1,
      },
    ],
  };

  // Client lifecycle chart
  const lifecycleChartData = {
    labels: mockClientLifecycle.map(item => item.stage),
    datasets: [
      {
        label: 'Client Count',
        data: mockClientLifecycle.map(item => item.count),
        backgroundColor: [
          '#722ed1',
          '#1890ff',
          '#52c41a',
          '#faad14',
          '#f5222d',
          '#ff7875',
        ],
      },
    ],
  };

  const getEngagementColor = (value: number, target: number) => {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return '#52c41a';
    if (percentage >= 80) return '#faad14';
    return '#f5222d';
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <AnalyticsCard
            title="Session Attendance"
            value={engagement.sessionAttendance}
            format="percentage"
            trend="up"
            change={3.2}
            subtitle="vs last month"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Goal Completion"
            value={engagement.goalCompletion}
            format="percentage"
            trend="up"
            change={5.1}
            subtitle="vs last month"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="App Usage"
            value={engagement.appUsage}
            format="percentage"
            trend="stable"
            change={-0.3}
            subtitle="vs last month"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Communication Frequency"
            value={engagement.communicationFrequency}
            format="percentage"
            trend="up"
            change={2.8}
            subtitle="vs last month"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <ChartContainer
            title={
              <span>
                <RocketOutlined /> Engagement Metrics vs Targets
              </span>
            }
            type="bar"
            data={engagementChartData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <ChartContainer
            title={
              <span>
                <TrophyOutlined /> Client Lifecycle Distribution
              </span>
            }
            type="pie"
            data={lifecycleChartData}
            height={300}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Engagement Performance Tracking">
            <Row gutter={[16, 16]}>
              {mockEngagementMetrics.map((metric, index) => (
                <Col span={8} key={index}>
                  <Card size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong>{metric.metric}</Text>
                        <Text style={{ color: getEngagementColor(metric.value, metric.target) }}>
                          {metric.value.toFixed(1)}%
                        </Text>
                      </div>
                      <Progress
                        percent={(metric.value / metric.target) * 100}
                        strokeColor={getEngagementColor(metric.value, metric.target)}
                        showInfo={false}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">Target: {metric.target.toFixed(1)}%</Text>
                        <Text 
                          style={{ 
                            color: metric.trend === 'up' ? '#52c41a' : 
                                   metric.trend === 'down' ? '#f5222d' : '#faad14' 
                          }}
                        >
                          {metric.change > 0 ? '+' : ''}{metric.change.toFixed(1)}%
                        </Text>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EngagementMetrics; 